import { z } from "zod";

import {
  egressLightSchema,
  fireDrillSchema,
  fireExtinguisherSchema,
  smokeDetectorSchema,
} from "../../../schemas";
import { Shifts } from "../types";
import { validateShift } from "../utils/validateShift";

export const monthlyNonBehavioralHealthFireDrillSchema = z
  .object({
    ...fireDrillSchema.shape,
    signalType: z.string(),
    fireParticipantsStaff: z.string().trim().min(1, "Required"),
    fireParticipantsPersonsSupported: z.string().trim().min(1, "Required"),
    meetingPlace: z.string().trim().min(1, "Required"),
    evacDurationMinutes: z.coerce.number().min(0),
    evacDurationSeconds: z.coerce
      .number()
      .min(0)
      .max(59, "Seconds must be less than 60"),
    exitUsed: z.string().trim().min(1, "Required"),
    drillStatus: z.string().trim().min(1, "Required"),
    drillStatusOther: z.string().optional(),
    fireComments: z.string().optional(),
    disasterStartTime: z.coerce.date(),
    disasterParticipantsStaff: z.string().trim().min(1, "Required"),
    disasterParticipantsPersonsSupported: z.string().trim().min(1, "Required"),
    disasterIssues: z.string().trim().min(1, "Required"),
    disasterIssueDescription: z.string().optional(),
    disasterComments: z.string().optional(),
    extinguishers: z.array(fireExtinguisherSchema),
    egressLights: z.array(egressLightSchema),
    smokeDetectors: z.array(smokeDetectorSchema),
    sprinklerHeadsStorage: z.string().min(1, "Required"),
    sprinklerChecked: z.string().trim().min(1, "Required"),
    sprinklerClearance: z.string().trim().min(1, "Required"),
    sprinklerIssues: z.string().trim().min(1, "Required"),
    sprinklerIssueDescription: z.string().optional(),
    waterTempLocation: z.string().trim().min(1, "Required"),
    waterTemperature: z.coerce.number().max(200),
    waterTempLocation2: z.string().trim().min(1, "Required"),
    waterTemperature2: z.coerce.number().max(200),
    hotWaterClearance: z.string().trim().min(1, "Required"),
    fireDepartmentConnection: z.string().trim().min(1, "Required"),
    exitVaried: z.string().trim().min(1, "Required"),
    firstAid: z.string().trim().min(1, "Required"),
    emergencyPlan: z.string().trim().min(1, "Required"),
    emergencyPlanFireChief: z.string().trim().min(1, "Required"),
    emergencyPlanAgencyContacts: z.string().trim().min(1, "Required"),
    emergencyPlanSupportedContacts: z.string().trim().min(1, "Required"),
    emergencySupplies: z.string().trim().min(1, "Required"),
    egressClearance: z.string().trim().min(1, "Required"),
    emergencyShutOff: z.string().trim().min(1, "Required"),
    alarm: z.string().trim().min(1, "Required"),
    alarmSent: z.string().trim().min(1, "Required"),
  })
  .superRefine(
    (
      {
        evacDurationSeconds,
        evacDurationMinutes,
        sprinklerIssues,
        sprinklerIssueDescription,
        auditDate,
        disasterStartTime,
      },
      refinementContext,
    ) => {
      if (evacDurationMinutes < 1 && evacDurationSeconds < 1) {
        refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid duration",
          path: ["evacDurationMinutes"],
        });
      }

      if (sprinklerIssues === "No") {
        if (
          sprinklerIssueDescription === undefined ||
          sprinklerIssueDescription.trim() === ""
        ) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["sprinklerIssueDescription"],
          });
        }
      }

      const drillStartTimeDiff =
        (disasterStartTime.getTime() - auditDate.getTime()) / 60000;

      if (drillStartTimeDiff < 10) {
        refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Disaster start time must be at least 10 minutes after fire drill.",
          path: ["disasterStartTime"],
        });
      }
    },
  );

export const fireDrillCreateSchema =
  monthlyNonBehavioralHealthFireDrillSchema.superRefine(
    ({ auditDate }, refinementContext) => {
      const date = new Date();
      if (date > new Date(date.getFullYear(), date.getMonth(), 5)) {
        if (auditDate < new Date(date.getFullYear(), date.getMonth(), 1)) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Date not in submission window",
            path: ["auditDate"],
          });
        }
      } else {
        if (auditDate < new Date(date.getFullYear(), date.getMonth() - 1, 1)) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Date not in submission window",
            path: ["auditDate"],
          });
        }
      }
    },
  );

export const fireDrillEditSchema = monthlyNonBehavioralHealthFireDrillSchema;

export const validateShiftSchema = z
  .object({
    auditDate: z.coerce.date(),
    shift: z.nativeEnum(Shifts),
  })
  .refine((data) => validateShift(data.auditDate, data.shift), {
    message: "Start time outside of shift.",
  });
